/**
 * Country Utility Functions
 * Helper functions for country detection and country-specific configurations
 */

/**
 * Supported countries list
 */
export const SUPPORTED_COUNTRIES = ['AU', 'IN', 'AE', 'US', 'SG', 'GB', 'MY'];

/**
 * Map timezone to country code
 */
const TIMEZONE_TO_COUNTRY = {
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
  'Australia/Darwin': 'AU',
  'Australia/Hobart': 'AU',
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Asia/Dubai': 'AE',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'Asia/Singapore': 'SG',
  'Europe/London': 'GB',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Jakarta': 'MY' // Indonesia, but close to Malaysia
};

/**
 * Map browser language to country code
 */
const LANGUAGE_TO_COUNTRY = {
  'en-AU': 'AU',
  'en-IN': 'IN',
  'hi-IN': 'IN',
  'ar-AE': 'AE',
  'en-AE': 'AE',
  'en-US': 'US',
  'en-SG': 'SG',
  'en-GB': 'GB',
  'ms-MY': 'MY',
  'en-MY': 'MY'
};

/**
 * Get country code from browser timezone
 */
const detectCountryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_TO_COUNTRY[timezone] || null;
  } catch (error) {
    console.warn('Failed to detect timezone:', error);
    return null;
  }
};

/**
 * Get country code from browser language
 */
const detectCountryFromLanguage = () => {
  try {
    const language = navigator.language || navigator.userLanguage;
    // Check full language code (e.g., en-GB)
    if (LANGUAGE_TO_COUNTRY[language]) {
      return LANGUAGE_TO_COUNTRY[language];
    }
    // Check language only (e.g., en)
    const langCode = language.split('-')[0];
    // For English, we can't determine country, so return null
    if (langCode === 'en') {
      // Check if there's a region hint
      const region = language.split('-')[1];
      if (region) {
        // Try to match common English variants
        if (region === 'AU') return 'AU';
        if (region === 'GB' || region === 'UK') return 'GB';
        if (region === 'US') return 'US';
        if (region === 'SG') return 'SG';
        if (region === 'MY') return 'MY';
      }
    }
    // Check other languages
    if (langCode === 'hi') return 'IN';
    if (langCode === 'ar') return 'AE';
    if (langCode === 'ms') return 'MY';
    return null;
  } catch (error) {
    console.warn('Failed to detect language:', error);
    return null;
  }
};

/**
 * Get country code from IP geolocation (via backend API)
 * This is called asynchronously on app load
 */
let detectedCountryCache = null;
let ipDetectionPromise = null;

export const detectCountryFromIP = async () => {
  if (detectedCountryCache) {
    return detectedCountryCache;
  }

  if (ipDetectionPromise) {
    return ipDetectionPromise;
  }

  ipDetectionPromise = (async () => {
    try {
    // Full URL: supports REACT_APP_API_URL as either https://host or https://host/api
    const getDetectCountryUrl = () => {
      const envApiUrl = process.env.REACT_APP_API_URL?.trim();
      if (envApiUrl) {
        const u = envApiUrl.replace(/\/$/, '');
        return u.endsWith('/api') ? `${u}/detect-country` : `${u}/api/detect-country`;
      }
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname.includes('nistylez.com')) {
          return 'https://api.nistylez.com/api/detect-country';
        }
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.')) {
          return 'http://localhost:5000/api/detect-country';
        }
      }
      return 'http://localhost:5000/api/detect-country';
    };

    const response = await fetch(getDetectCountryUrl(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.countryCode) {
        const countryCode = data.countryCode.toUpperCase();
        if (SUPPORTED_COUNTRIES.includes(countryCode)) {
          detectedCountryCache = countryCode;
          localStorage.setItem('detectedCountryCode', countryCode);
          return countryCode;
        }
      }
    }
    } catch (error) {
      console.warn('Failed to detect country from IP:', error);
    }
    return null;
  })();

  try {
    return await ipDetectionPromise;
  } finally {
    ipDetectionPromise = null;
  }
};

/**
 * Get country code from various sources
 * Priority: logged-in user profile > localStorage > detected IP > timezone > language > default
 */
export const getCountryCode = () => {
  if (typeof window === 'undefined') return 'AU';

  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('customerUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      const fromProfile = user?.location?.country || user?.country;
      if (fromProfile && SUPPORTED_COUNTRIES.includes(String(fromProfile).trim().toUpperCase())) {
        return String(fromProfile).trim().toUpperCase();
      }
    }
  } catch (e) {
    // ignore
  }
  
  // From localStorage (manual selection or synced)
  const stored = localStorage.getItem('countryCode');
  if (stored && SUPPORTED_COUNTRIES.includes(stored.toUpperCase())) {
    return stored.toUpperCase();
  }
  
  // From detected IP (if cached)
  const detected = localStorage.getItem('detectedCountryCode');
  if (detected && SUPPORTED_COUNTRIES.includes(detected.toUpperCase())) {
    return detected.toUpperCase();
  }
  
  // Priority 3: From browser timezone
  const timezoneCountry = detectCountryFromTimezone();
  if (timezoneCountry) {
    return timezoneCountry;
  }
  
  // Priority 4: From browser language
  const languageCountry = detectCountryFromLanguage();
  if (languageCountry) {
    return languageCountry;
  }
  
  // Priority 5: Default
  return 'AU';
};

/**
 * Resolve country synchronously (profile / localStorage / timezone / default).
 * Persists to localStorage so the first API call never waits on IP detection.
 */
export const ensureSyncCountryCode = () => {
  if (localStorage.getItem('countryCode')) {
    return getCountryCode();
  }

  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('customerUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      const profileCountry = user?.location?.country || user?.country;
      if (profileCountry) {
        const code = String(profileCountry).trim().toUpperCase();
        if (SUPPORTED_COUNTRIES.includes(code)) {
          localStorage.setItem('countryCode', code);
          localStorage.setItem('detectedCountryCode', code);
          return code;
        }
      }
    }
  } catch (e) {
    // ignore malformed user payload
  }

  const code = getCountryCode();
  localStorage.setItem('countryCode', code);
  localStorage.setItem('detectedCountryCode', code);
  return code;
};

/** Singleton promise so App + Home share one detection run per page load */
let countryInitPromise = null;

/**
 * Initialize country detection on app load (IP refinement — non-blocking).
 * Dispatches app:country-changed when IP resolves a different market than the sync fallback.
 */
export const initializeCountryDetection = () => {
  if (countryInitPromise) {
    return countryInitPromise;
  }

  countryInitPromise = (async () => {
    const syncCode = ensureSyncCountryCode();

    const ipCountry = await detectCountryFromIP();
    if (ipCountry && ipCountry !== syncCode) {
      localStorage.setItem('countryCode', ipCountry);
      localStorage.setItem('detectedCountryCode', ipCountry);
      window.dispatchEvent(
        new CustomEvent('app:country-changed', { detail: { countryCode: ipCountry } })
      );
      return ipCountry;
    }

    return getCountryCode();
  })();

  return countryInitPromise;
};

/**
 * Country-specific field labels and configurations
 */
export const COUNTRY_FIELDS = {
  AU: {
    currency: 'AUD',
    currencySymbol: '$',
    timezone: 'Australia/Sydney'
  },
  IN: {
    currency: 'INR',
    currencySymbol: '₹',
    timezone: 'Asia/Kolkata'
  },
  AE: { // UAE
    currency: 'AED',
    currencySymbol: 'AED',
    timezone: 'Asia/Dubai'
  },
  US: { // USA
    currency: 'USD',
    currencySymbol: '$',
    timezone: 'America/New_York'
  },
  SG: { // Singapore
    currency: 'SGD',
    currencySymbol: 'S$',
    timezone: 'Asia/Singapore'
  },
  GB: { // United Kingdom
    currency: 'GBP',
    currencySymbol: '£',
    timezone: 'Europe/London'
  },
  MY: { // Malaysia
    currency: 'MYR',
    currencySymbol: 'RM',
    timezone: 'Asia/Kuala_Lumpur'
  }
};

/**
 * Get country-specific field configuration
 * Returns AU config as default fallback if country not found
 */
export const getCountryFields = (countryCode) => {
  const code = (countryCode || getCountryCode()).toUpperCase();
  return COUNTRY_FIELDS[code] || COUNTRY_FIELDS['AU'];
};

/**
 * Check if country is supported
 */
export const isCountrySupported = (countryCode) => {
  if (!countryCode) return false;
  return SUPPORTED_COUNTRIES.includes(countryCode.toUpperCase());
};

/**
 * Get list of supported countries with labels
 */
export const getSupportedCountriesList = () => {
  return [
    { code: 'AU', name: 'Australia', currency: 'AUD' },
    { code: 'IN', name: 'India', currency: 'INR' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'AED' },
    { code: 'US', name: 'United States', currency: 'USD' },
    { code: 'SG', name: 'Singapore', currency: 'SGD' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
    { code: 'MY', name: 'Malaysia', currency: 'MYR' }
  ];
};

/**
 * Format currency amount based on country
 */
export const formatCurrency = (amount, countryCode) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    amount = 0;
  }
  const code = countryCode || getCountryCode();
  const fields = getCountryFields(code);
  const formatted = Number(amount).toFixed(2);
  
  // Currency symbol always comes before amount for all countries
  // Examples: "₹ 100.00" (India), "AED 100.00" (UAE), "$ 100.00" (Australia/USA)
  return `${fields.currencySymbol} ${formatted}`;
};
